import React, { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAtivoLogs, AtivoLogsFilters } from '@/hooks/useAtivoLogs';
import { Loader2, Clock, User, Edit, Filter, X } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface AtivoLogsProps {
  ativoId: string;
}

export const AtivoLogs = ({ ativoId }: AtivoLogsProps) => {
  const [filters, setFilters] = useState<AtivoLogsFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  const { data: logs, isLoading } = useAtivoLogs(ativoId, filters);

  // Extrair usuários únicos dos logs para o filtro
  const uniqueUsers = useMemo(() => {
    if (!logs) return [];
    const users = logs
      .filter(log => log.usuario)
      .map(log => log.usuario!)
      .filter((user, index, self) => 
        index === self.findIndex(u => u.id === user.id)
      );
    return users;
  }, [logs]);

  // Extrair campos únicos dos logs para o filtro
  const uniqueFields = useMemo(() => {
    if (!logs) return [];
    const fields = logs
      .filter(log => log.campo_alterado)
      .map(log => log.campo_alterado!)
      .filter((field, index, self) => self.indexOf(field) === index);
    return fields;
  }, [logs]);

  const handleClearFilters = () => {
    setFilters({});
  };

  const hasActiveFilters = Object.values(filters).some(value => value);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <Card className="p-6 text-center text-muted-foreground">
        <p>Nenhum histórico de alterações encontrado</p>
      </Card>
    );
  }

  const getFieldLabel = (field?: string) => {
    const labels: Record<string, string> = {
      nome: 'Nome',
      status_operacional: 'Status Operacional',
      tipo_id: 'Tipo',
      ambiente: 'Ambiente',
      localizacao_id: 'Localização',
      fabricante_id: 'Fabricante',
      proprietario_id: 'Proprietário',
      grupo_responsavel_id: 'Grupo Responsável',
      valor_aquisicao: 'Valor de Aquisição',
      atributos_customizados: 'Atributos Customizados',
    };
    return labels[field || ''] || field;
  };

  return (
    <div className="space-y-4">
      {/* Botão de toggle dos filtros */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="gap-2"
        >
          <Filter className="h-4 w-4" />
          {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
        </Button>
        
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="gap-2"
          >
            <X className="h-4 w-4" />
            Limpar Filtros
          </Button>
        )}
      </div>

      {/* Painel de filtros */}
      {showFilters && (
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Filtro por usuário */}
            <div className="space-y-2">
              <Label>Usuário</Label>
              <Select
                value={filters.usuarioId || ''}
                onValueChange={(value) => 
                  setFilters(prev => ({ ...prev, usuarioId: value || undefined }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os usuários" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todos os usuários</SelectItem>
                  {uniqueUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filtro por campo */}
            <div className="space-y-2">
              <Label>Campo Alterado</Label>
              <Select
                value={filters.campoAlterado || ''}
                onValueChange={(value) => 
                  setFilters(prev => ({ ...prev, campoAlterado: value || undefined }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os campos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todos os campos</SelectItem>
                  {uniqueFields.map((field) => (
                    <SelectItem key={field} value={field}>
                      {getFieldLabel(field)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filtro por data inicial */}
            <div className="space-y-2">
              <Label>Data Inicial</Label>
              <Input
                type="date"
                value={filters.dataInicio || ''}
                onChange={(e) => 
                  setFilters(prev => ({ ...prev, dataInicio: e.target.value || undefined }))
                }
              />
            </div>

            {/* Filtro por data final */}
            <div className="space-y-2">
              <Label>Data Final</Label>
              <Input
                type="date"
                value={filters.dataFim || ''}
                onChange={(e) => 
                  setFilters(prev => ({ ...prev, dataFim: e.target.value || undefined }))
                }
              />
            </div>
          </div>
        </Card>
      )}

      {/* Lista de logs */}
      <ScrollArea className="h-[400px] pr-4">
        <div className="space-y-3">
        {logs.map((log) => (
          <Card key={log.id} className="p-4 hover:bg-accent/50 transition-colors">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-primary/10 mt-0.5">
                {log.tipo === 'criacao' ? (
                  <User className="h-4 w-4 text-primary" />
                ) : (
                  <Edit className="h-4 w-4 text-primary" />
                )}
              </div>
              
              <div className="flex-1 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-sm">{log.acao}</p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                    <Clock className="h-3 w-3" />
                    {format(new Date(log.criado_em), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </div>
                </div>
                
                {log.campo_alterado && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Campo: </span>
                    <span className="font-medium">{getFieldLabel(log.campo_alterado)}</span>
                  </div>
                )}
                
                {(log.valor_anterior || log.valor_novo) && (
                  <div className="grid grid-cols-2 gap-3 mt-2 text-xs">
                    {log.valor_anterior && (
                      <div className="space-y-1">
                        <span className="text-muted-foreground">Valor Anterior:</span>
                        <div className="p-2 bg-destructive/10 border border-destructive/20 rounded-md">
                          <p className="font-mono break-all line-clamp-2">{log.valor_anterior}</p>
                        </div>
                      </div>
                    )}
                    {log.valor_novo && (
                      <div className="space-y-1">
                        <span className="text-muted-foreground">Valor Novo:</span>
                        <div className="p-2 bg-success/10 border border-success/20 rounded-md">
                          <p className="font-mono break-all line-clamp-2">{log.valor_novo}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {log.usuario && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2">
                    <User className="h-3 w-3" />
                    <span>por {log.usuario.name}</span>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
        </div>
      </ScrollArea>
    </div>
  );
};
